import { useState, useCallback, useMemo, useRef } from 'react';
import { IoChevronBack } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import useAxios
  from '../../../../axios/useAxios';
import { createFidelityCard } from '../../../../services/fidelityService';
import StepProgressBar from '../../../../components/progressBar/StepProgressBar';
import { PikkoBulle } from './components/pikkoBulle';
import PrimaryButton from '../../../../components/button/PrimaryButton';
import InitialChoiceScreen from './components/InitialChoiceScreen';
import AddPhysicalCard from './components/AddPhysicalCard';
import SuccessScreen from './components/SuccessScreen';
import ErrorMessage from './components/ErrorMessage';
import CreationStepRenderer from './components/CreationStepRenderer';
import { validateField, isCardNumberValid } from './utils/validation';
import { initializeFormData } from './utils/formInitializers';
import pikko3d from '../../../../styles/img/Pikko3D.svg';
import { SCAN, USER_FID_CARD, USER_FID_CARD_DETAIL } from '../../../../constants/routes';
import { isValidPhone } from '../../../../utils/phoneNumberCheck';
import { getJustPhoneNumber } from './utils/validation';
import { useFidCardFunnelTracking } from '../../../../posthog/funnelTrackingFidCard';
import { useEffect } from 'react';
import BackButton from '../../../../components/button/BackButton';
const INITIAL_STEP_INDEX = 0;
const DEFAULT_DIAL_CODE = '33';
const CREATION_STEPS_COUNT = 6;

const PATHS = {
  ADD: 'add',
  CREATE: 'create'
};

const STEP_VALIDATION_MAP = {
  0: (formData, errors) => formData.fullName.trim() && !errors.fullName,
  1: (formData) => formData.civility !== '',
  2: (formData, errors) => formData.birthDate.day && formData.birthDate.month && formData.birthDate.year && !errors.birthDate,
  3: (formData, errors) => formData.email && !errors.email,
  4: (formData, errors) => formData.phone && !errors.phone,
  5: (formData) => formData.store !== ''
};

const CREATION_STEPS = [
  { message: "Ok ! 😊 Tu peux me donner ton prénom et ton nom ?", pikkoImage: pikko3d },
  { message: "Quelle est ta civilité ?", pikkoImage: pikko3d },
  { message: "Pour une petite surprise le jour J, quelle est ta date de naissance ? 🎂", pikkoImage: pikko3d },
  { message: "Un petit email pour continuer", pikkoImage: pikko3d },
  { message: "Courage plus que 2 étapes ✊ ton numéro de téléphone ?", pikkoImage: pikko3d },
  { message: "Un pti dernier, quel est ton magasin MATCH préféré ? Tu as l'embarras du choix ! 😉", pikkoImage: pikko3d }
];

const ADD_CARD_STEPS = [{
  message: "D'accord, ajoutons ta carte physique !",
  pikkoImage: pikko3d
}];

const useFidelityCardForm = (user) => {
  const [formData, setFormData] = useState(() => initializeFormData(user));
  const [errors, setErrors] = useState({
    fullName: '',
    email: '',
    phone: '',
    store: ''
  });

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    const error = validateField(fieldName, value);
    setErrors(prev => ({ ...prev, [fieldName]: error }));
  }, []);

  const validateAllFields = useCallback((dialCode) => {
    const validationErrors = {};

    for (const field of Object.keys(formData)) {
      const error = validateField(field, formData[field]);
      if (error) {
        validationErrors[field] = error;
      }
    }

    const justPhoneNumber = getJustPhoneNumber(formData.phone, dialCode);
    if (!justPhoneNumber) {
      validationErrors.phone = "Le numéro de téléphone est invalide";
    }

    return { validationErrors, justPhoneNumber };
  }, [formData]);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    handleFieldChange,
    validateAllFields
  };
};

const useFidelityCardSubmission = (axiosInstance, userId, storeId, setIsLoading, setIsSuccess, dispatch) => {
  const { trackSuccess, trackError } = useFidCardFunnelTracking()
  const [submitError, setSubmitError] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const errorMessage = "Une erreur est survenue lors de la création de la carte de fidélité";

  const submitForm = useCallback(async (formData) => {
    setIsLoading(true);
    setSubmitError('');
    setErrorDetails(null);

    try {
      const result = await createFidelityCard(formData, axiosInstance, userId, storeId, dispatch);
      if (result.success) {
        setIsSuccess(true);
        trackSuccess({
          formData
        })
      } else {
        setSubmitError(result.message);
        setErrorDetails({
          errorCode: result.errorCode,
          isServerError: result.isServerError,
          shouldRetry: result.shouldRetry
        });
        trackError({
          errorCode: result.errorCode,
          isServerError: result.isServerError,
          shouldRetry: result.shouldRetry
        })
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setSubmitError(errorMessage);
      setErrorDetails({
        errorCode: null,
        isServerError: false,
        shouldRetry: true
      });
    } finally {
      setIsLoading(false);
    }
  }, [userId, storeId, setIsLoading, setIsSuccess, dispatch]);

  const retrySubmission = useCallback(async (formData) => {
    await submitForm(formData);
  }, [submitForm]);

  return { submitError, errorDetails, submitForm, retrySubmission };
};

const FidCardScreen = ({
  onConfirm,
  onBack,
  goToScan,
  companyLogo,
  isLoading,
  setIsLoading,
  isSuccess,
  setIsSuccess,
  previousRoute,
  choice,
  companyId
}) => {
  const [fidCardNum, setFidCardNum] = useState(undefined);
  const [currentStepIndex, setCurrentStepIndex] = useState(INITIAL_STEP_INDEX);
  const [selectedPath, setSelectedPath] = useState(null);
  const [choiceValue, setChoiceValue] = useState(null);
  const [isOneChoicePossible, setIsOneChoicePossible] = useState(false);
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  const state = useLocation().state;
  const axiosInstance = useAxios();
  const dispatch = useDispatch();

  const { trackSuccess } = useFidCardFunnelTracking()

  const scrollToTop = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const formRef = useRef(null);

  const handlePathSelection = useCallback((path) => {
    setSelectedPath(path);
    setCurrentStepIndex(INITIAL_STEP_INDEX);
  }, []);

  const handleChoiceValueChange = useCallback((value) => {
    setChoiceValue(value);
    if (isOneChoicePossible) {
      handlePathSelection(value);
    }
  }, [isOneChoicePossible, handlePathSelection]);

  useEffect(() => {
    scrollToTop(formRef);

    if (choice === "add") {
      handlePathSelection(PATHS.ADD);
    }
  }, [choice]);

  const {
    formData,
    setFormData,
    errors,
    setErrors,
    handleFieldChange,
    validateAllFields
  } = useFidelityCardForm(user);

  const { submitError, errorDetails, submitForm, retrySubmission } = useFidelityCardSubmission(
    axiosInstance,
    user?._id,
    formData.store,
    setIsLoading,
    setIsSuccess,
    dispatch
  );

  const isStepValid = useCallback(() => {
    const validator = STEP_VALIDATION_MAP[currentStepIndex];
    return validator ? validator(formData, errors) : true;
  }, [currentStepIndex, formData, errors]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > INITIAL_STEP_INDEX) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else if (selectedPath) {
      if (previousRoute) {
        navigate(previousRoute)
      }
      setSelectedPath(null);
      setCurrentStepIndex(INITIAL_STEP_INDEX);
    } else {
      onBack();
    }
  }, [currentStepIndex, selectedPath, previousRoute]);

  const handleComplete = useCallback(async () => {
    const { validationErrors, justPhoneNumber } = validateAllFields(dialCode);

    if (Object.keys(validationErrors).length === 0) {
      const submissionData = {
        ...formData,
        justPhoneNumber,
        indicatif: dialCode
      };
      await submitForm(submissionData);
      trackSuccess({
        data: formData
      })
    } else {
      setErrors(validationErrors);
      setIsLoading(false);
    }
  }, [validateAllFields, dialCode, formData, submitForm, setErrors, setIsLoading]);

  const currentSteps = useMemo(() => {
    return selectedPath === PATHS.CREATE ? CREATION_STEPS : ADD_CARD_STEPS;
  }, [selectedPath]);

  const totalSteps = useMemo(() => {
    return selectedPath === PATHS.CREATE ? CREATION_STEPS_COUNT : 1;
  }, [selectedPath]);

  const shouldShowNextButton = useMemo(() => {
    if (!selectedPath) return false;
    if (selectedPath === PATHS.ADD) return true;
    return currentStepIndex < CREATION_STEPS.length - 1;
  }, [selectedPath, currentStepIndex]);

  const handleNextStep = useCallback(() => {
    setCurrentStepIndex(currentStepIndex + 1);
  }, [currentStepIndex]);

  const handleAddCardConfirm = useCallback(() => {
    onConfirm(fidCardNum);
  }, [onConfirm, fidCardNum]);

  const navigateToCardDetail = useCallback(() => {
    if (previousRoute && previousRoute !== USER_FID_CARD) {
      navigate(SCAN, { state: { previousRoute: location.pathname } })
    }
    navigate(USER_FID_CARD);
  }, [navigate]);

  const renderNextButton = () => {
    if (!shouldShowNextButton) return null;

    const isValid = isStepValid();
    const isAddPath = selectedPath === PATHS.ADD;
    const isCardValid = isCardNumberValid(fidCardNum);

    const buttonProps = {
      label: "Suivant",
      isLoading,
      className: `w-full transition-all duration-300 ${(isAddPath ? isCardValid : isValid)
        ? ''
        : 'opacity-50 cursor-not-allowed grayscale hover:grayscale'
        }`
    };

    if (isAddPath) {
      return (
        <PrimaryButton
          {...buttonProps}
          onClick={handleAddCardConfirm}
          disabled={!isCardValid}
        />
      );
    }

    return (
      <PrimaryButton
        {...buttonProps}
        onClick={handleNextStep}
        disabled={!isValid}
      />
    );
  };

  if (isSuccess) {
    return <SuccessScreen onConfirm={navigateToCardDetail} />;
  }

  const renderMainContent = () => {
    if (!selectedPath) {
      return (
        <InitialChoiceScreen
          onPathSelection={handlePathSelection}
          choiceValue={choiceValue}
          setChoiceValue={handleChoiceValueChange}
          isLoading={isLoading}
          isOneChoicePossible={isOneChoicePossible}
          setIsOneChoicePossible={setIsOneChoicePossible}
        />
      );
    }

    if (selectedPath === PATHS.ADD) {
      return (
        <AddPhysicalCard
          fidCardNum={fidCardNum}
          setFidCardNum={setFidCardNum}
          companyLogo={companyLogo}
          goToScan={goToScan}
        />
      );
    }

    return (
      <div className="mt-8 h-full px-4">
        <CreationStepRenderer
          currentStepIndex={currentStepIndex}
          formData={formData}
          errors={errors}
          handleFieldChange={handleFieldChange}
          setFormData={setFormData}
          isValidPhone={isValidPhone}
          companyId={state?.fidCard?.companyId || companyId}
          handleComplete={handleComplete}
          isLoading={isLoading}
          dialCode={dialCode}
          setDialCode={setDialCode}
        />
      </div>
    );
  };

  return (
    <div ref={formRef} className="flex flex-col justify-start relative top-0 px-6 bg-gradient2 h-screen w-screen py-[20px]">
      <div className="flex items-center gap-4 mb-6">
        <BackButton onClick={handleBack} />
        {selectedPath && (
          <StepProgressBar
            currentStep={currentStepIndex}
            totalSteps={totalSteps}
          />
        )}
      </div>

      <div className="flex justify-start w-full px-4">
        <PikkoBulle
          steps={currentSteps}
          currentStepIndex={currentStepIndex}
        />
      </div>

      {renderMainContent()}

      <div className="mt-auto bottom-0 left-0 right-0 pb-4">
        {submitError && <ErrorMessage message={submitError} />}

        <div className="flex justify-center mb-10">
          {renderNextButton()}
        </div>

        {currentStepIndex === INITIAL_STEP_INDEX && (
          <div className="text-center text-sm mb-4">
            <p className="text-gray-600">
              En continuant, tu acceptes les politiques de confidentialité ainsi que nos{' '}
              conditions générales d'utilisation.
              Merci de ta confiance !
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FidCardScreen;
